import React, { useEffect, useRef, useState } from 'react';
import css from './FreeviewSignupModal.module.css';
import { apiBaseUrl } from '../../../../util/api';

const FreeviewSignupModal = ({ onClose }) => {
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(true);
  const [selectedRegions, setSelectedRegions] = useState([]);

  // lpasByRegion: { [regionId]: string[] } — all available LPAs for a region (fetched on demand)
  const [lpasByRegion, setLpasByRegion] = useState({});
  // loadingLpas: set of regionIds currently being fetched
  const [loadingLpas, setLoadingLpas] = useState(new Set());
  // lpaSelectionsByRegion: { [regionId]: string[] } — checked LPA names per region
  const [lpaSelectionsByRegion, setLpaSelectionsByRegion] = useState({});
  // expandedRegions: set of regionIds whose LPA panel is open
  const [expandedRegions, setExpandedRegions] = useState(new Set());
  const fetchedLpaRegions = useRef(new Set());

  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${apiBaseUrl()}/api/leadflow/freeview-regions`)
      .then(r => r.json())
      .then(data => setRegions(data.data || []))
      .catch(() => setRegions([]))
      .finally(() => setLoadingRegions(false));
  }, []);

  const fetchLpasForRegion = regionId => {
    if (fetchedLpaRegions.current.has(regionId)) return;
    fetchedLpaRegions.current.add(regionId);
    setLoadingLpas(prev => new Set(prev).add(regionId));
    fetch(`${apiBaseUrl()}/api/leadflow/freeview-region-lpas/${regionId}`)
      .then(r => r.json())
      .then(data => setLpasByRegion(prev => ({ ...prev, [regionId]: data.data || [] })))
      .catch(() => {
        fetchedLpaRegions.current.delete(regionId);
      })
      .finally(() =>
        setLoadingLpas(prev => { const s = new Set(prev); s.delete(regionId); return s; })
      );
  };

  const toggleRegion = id => {
    const nowSelected = selectedRegions.includes(id)
      ? selectedRegions.filter(r => r !== id)
      : [...selectedRegions, id];

    setSelectedRegions(nowSelected);

    if (nowSelected.includes(id)) {
      // Region just selected — fetch LPAs so user can optionally restrict
      fetchLpasForRegion(id);
      setExpandedRegions(prev => new Set(prev).add(id));
    } else {
      // Region was deselected — remove its LPA selections and collapse
      setLpaSelectionsByRegion(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setExpandedRegions(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  const toggleLpaExpand = regionId => {
    if (!expandedRegions.has(regionId)) {
      fetchLpasForRegion(regionId);
    }
    setExpandedRegions(prev => {
      const s = new Set(prev);
      s.has(regionId) ? s.delete(regionId) : s.add(regionId);
      return s;
    });
  };

  const toggleLpa = (regionId, lpaName) => {
    setLpaSelectionsByRegion(prev => {
      const current = prev[regionId] || [];
      const updated = current.includes(lpaName)
        ? current.filter(n => n !== lpaName)
        : [...current, lpaName];
      return { ...prev, [regionId]: updated };
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!form.firstName || !form.lastName || !form.email || !form.password) {
      setError('Please fill in all required fields.');
      return;
    }

    const regionsWithoutLpas = selectedRegions.filter(
      id => !lpaSelectionsByRegion[id] || lpaSelectionsByRegion[id].length === 0
    );
    if (selectedRegions.length > 0 && regionsWithoutLpas.length > 0) {
      const names = regionsWithoutLpas
        .map(id => regions.find(r => r.id === id)?.name || id)
        .join(', ');
      setError(`Please select at least one LPA for: ${names}`);
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    const lpaSelections = Object.entries(lpaSelectionsByRegion)
      .filter(([, names]) => names.length > 0)
      .map(([regionId, lpaNames]) => ({ regionId, lpaNames }));

    setSubmitting(true);
    try {
      const res = await fetch(`${apiBaseUrl()}/api/leadflow/freeview-signup`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          password: form.password,
          regionIds: selectedRegions,
          lpaSelections,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Signup failed. Please try again.');
        return;
      }

      if (data.redirectUrl) {
        if (typeof window !== 'undefined') {
          window.open(data.redirectUrl, '_blank');
          onClose();
          window.location.reload();
        }
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedRegionObjects = regions.filter(r => selectedRegions.includes(r.id));

  return (
    <div className={css.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={css.modal}>
        <button className={css.closeBtn} onClick={onClose} aria-label="Close">✕</button>

        <h2 className={css.title}>Get free access to live projects</h2>
        <p className={css.subtitle}>
          See planning applications in your area — no credit card needed.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className={css.field} style={{ flex: 1 }}>
              <label className={css.label}>First name *</label>
              <input
                className={css.input}
                type="text"
                placeholder="John"
                value={form.firstName}
                onChange={e => { const v = e.target.value; setForm(f => ({ ...f, firstName: v })); }}
              />
            </div>
            <div className={css.field} style={{ flex: 1 }}>
              <label className={css.label}>Last name *</label>
              <input
                className={css.input}
                type="text"
                placeholder="Smith"
                value={form.lastName}
                onChange={e => { const v = e.target.value; setForm(f => ({ ...f, lastName: v })); }}
              />
            </div>
          </div>

          <div className={css.field}>
            <label className={css.label}>Email *</label>
            <input
              className={css.input}
              type="email"
              placeholder="john@example.com"
              value={form.email}
              onChange={e => { const v = e.target.value; setForm(f => ({ ...f, email: v })); }}
            />
          </div>

          <div className={css.field}>
            <label className={css.label}>Password *</label>
            <input
              className={css.input}
              type="password"
              placeholder="Min. 8 characters"
              value={form.password}
              onChange={e => { const v = e.target.value; setForm(f => ({ ...f, password: v })); }}
            />
          </div>

          <span className={css.regionLabel}>Your regions (select all that apply)</span>
          {loadingRegions ? (
            <p className={css.loadingRegions}>Loading regions…</p>
          ) : (
            <div className={css.regionGrid}>
              {regions.map(r => (
                <button
                  key={r.id}
                  type="button"
                  className={`${css.regionChip} ${selectedRegions.includes(r.id) ? css.regionChipSelected : ''}`}
                  onClick={() => toggleRegion(r.id)}
                >
                  {r.name}
                </button>
              ))}
            </div>
          )}

          {/* LPA selection — shown per selected region */}
          {selectedRegionObjects.length > 0 && (
            <div className={css.lpaSection}>
              <span className={css.lpaLabel}>
                Select LPAs to view projects <span className={css.lpaLabelOptional}>(at least one per region)</span>
              </span>
              {selectedRegionObjects.map(region => {
                const isExpanded = expandedRegions.has(region.id);
                const isLoading = loadingLpas.has(region.id);
                const lpas = lpasByRegion[region.id] || [];
                const selected = lpaSelectionsByRegion[region.id] || [];

                return (
                  <div key={region.id} className={css.lpaRegionBlock}>
                    <button
                      type="button"
                      className={css.lpaRegionHeader}
                      onClick={() => toggleLpaExpand(region.id)}
                    >
                      <span className={css.lpaRegionName}>{region.name}</span>
                      <span className={css.lpaRegionMeta}>
                        {selected.length > 0
                          ? `${selected.length} LPA${selected.length !== 1 ? 's' : ''} selected`
                          : 'Select LPAs to view projects'}
                        <span className={css.lpaChevron}>{isExpanded ? '▲' : '▼'}</span>
                      </span>
                    </button>

                    {isExpanded && (
                      <div className={css.lpaList}>
                        {isLoading && <p className={css.lpaLoading}>Loading LPAs…</p>}
                        {!isLoading && lpas.length === 0 && (
                          <p className={css.lpaLoading}>No LPAs found.</p>
                        )}
                        {!isLoading && lpas.length > 0 && (
                          <div className={css.lpaSelectAllRow}>
                            <button
                              type="button"
                              className={css.lpaSelectAllBtn}
                              onClick={() => {
                                const allSelected = lpas.every(name => selected.includes(name));
                                setLpaSelectionsByRegion(prev => ({
                                  ...prev,
                                  [region.id]: allSelected ? [] : [...lpas],
                                }));
                              }}
                            >
                              {lpas.every(name => selected.includes(name)) ? 'Deselect all' : 'Select all'}
                            </button>
                          </div>
                        )}
                        {!isLoading && lpas.map(lpaName => {
                          const checked = selected.includes(lpaName);
                          return (
                            <label key={lpaName} className={`${css.lpaItem} ${checked ? css.lpaItemChecked : ''}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleLpa(region.id, lpaName)}
                                className={css.lpaCheckbox}
                              />
                              <span className={css.lpaName}>{lpaName}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {error && <p className={css.error}>{error}</p>}

          <button type="submit" className={css.submitBtn} disabled={submitting}>
            {submitting ? 'Creating your account…' : 'View live projects free →'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FreeviewSignupModal;
